'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      window.location.href = '/';
    } catch (err: any) {
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - branding */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 50%, #0EA5E9 100%)' }}
      >
        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-8 w-48 h-48 rounded-full bg-white/5" />

        <div className="relative z-10 flex items-center gap-3">
          <AppLogo size={36} />
          <span className="text-2xl font-bold tracking-tight text-white">YAFEX</span>
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white leading-tight mb-4">
            Industrial Diagnostic<br />Intelligence Platform
          </h2>
          <p className="text-blue-200 text-base leading-relaxed max-w-sm">
            AI-powered fault diagnosis and maintenance intelligence for industrial assets. Reduce downtime, improve reliability.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            {[
              { icon: 'ChatBubbleLeftRightIcon', label: 'AI Diagnostic Chat' },
              { icon: 'ChartBarIcon', label: 'Breakdown Analysis' },
              { icon: 'CpuChipIcon', label: 'Asset Management' },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-white" />
                </div>
                <span className="text-sm text-blue-100 font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-xs text-blue-300 font-mono">YAFEX v1.0 · YAFE Internal Platform</p>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={32} />
              <span className="text-xl font-bold tracking-tight text-foreground">YAFEX</span>
            </div>
            <p className="text-xs text-muted-foreground font-mono tracking-widest uppercase">
              Industrial Diagnostic Intelligence
            </p>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back</h1>
            <p className="text-sm text-muted-foreground">
              Sign in to your YAFEX account
            </p>
          </div>

          {/* Login card */}
          <div className="card p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  className="input-field font-mono"
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="input-field font-mono pr-10"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={15} />
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-negative/20 bg-negative/5">
                  <Icon name="ExclamationTriangleIcon" size={14} className="text-negative flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-negative">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center mt-2"
              >
                {loading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    <Icon name="ArrowRightEndOnRectangleIcon" size={14} />
                    Sign in
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6 font-mono">
            Access is provisioned by YAFE staff. Contact your administrator if you need an account.
          </p>
        </div>
      </div>
    </div>
  );
}
